import { Component, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Router } from '@angular/router';
import moment from 'moment';
import { getPagination, PaginationParams } from 'src/app/common/pagination/pagination.service';
import { NoveltyService } from '../../services/novelty.service';
import { AccessManagerService } from '../../../auth/accessManager/accessManager.service';
import { ModalDialogComponent } from 'src/app/common/modal/modalDialog.component';
import { NoveltyModel } from '../../models/novelty.model';
import { ImpersonationService } from 'src/app/auth/apigee/impersonation.service';

@Component({
    selector: 'app-novelty-maintenance',
    templateUrl: './novelty-maintenance.component.html',
    styleUrls: ['./novelty-maintenance.component.css'],
    standalone: false
})
export class NoveltyMaintenanceComponent implements OnInit {

  @ViewChild(NgbDropdown) private dropdown: NgbDropdown;
  @ViewChild(ModalDialogComponent, { static: true }) private deleteNoveltyDialog: ModalDialogComponent;

  public get impersonationEnabled(): boolean {
    return this.impersonationService.canImpersonate;
  }

  // Loading
  loading: boolean = true;

  // Listado
  novelties: NoveltyModel[] = [];
  noveltiesPagination: NoveltyModel[][] = [];
  paginationParams = new PaginationParams(30, 1, 0);
  selectedNovelty: NoveltyModel = null;

  // Formulario
  form: FormGroup = null;
  fileContent: number[];
  fileName: string;
  titleForm: string;

  // Mensajes
  private timeout: any;
  message = {
    show: false,
    type: '',
    body: ''
  };

  // Ordenamiento
  currentFilterParams = {
    properties: 'iDate',
    typeOrder: 'ASC'
  };

  constructor(
    private impersonationService: ImpersonationService,
    private noveltyService: NoveltyService,
    private accessManagerService: AccessManagerService,
    private router: Router) {
  }

  ngOnInit(): void {
    this.accessManagerService.getResourcePermissions(4096).then(x => {
      const action = x.mResourceActions.find(r => r.ActionTypeId == 2);
      if (action && action.AllowChk) {
        this.getInitData();
      }
      else {
        this.router.navigate(['/']);
      }
    }).catch((e) => {
      console.log(e);
    });
  }

  ngAfterViewInit(): void {
    this.deleteNoveltyDialog.acceptEvent.subscribe(() => {
      this.deleteNovelty();
    });
  }

  ngOnDestroy(): void {
    this.deleteNoveltyDialog.acceptEvent.unsubscribe();
  }

  /**
   * Consulta los datos iniciales
   */
  getInitData(): void {
    this.loading = true;
    this.selectedNovelty = null;

    this.noveltyService.getAll().subscribe(results => {
      // Formateo de fechas
      results.novelties.forEach(x => {
        x.begDate = moment(x.begDate).format("DD/MM/YYYY");
        x.endDate = moment(x.endDate).format("DD/MM/YYYY");
      });

      this.novelties = results.novelties.sort((a, b) => moment(b.iDate).valueOf() - moment(a.iDate).valueOf());
      this.noveltiesPagination[0] = [];
      this.noveltiesPagination = getPagination(this.paginationParams, this.novelties);
      this.loading = false;
    }, () => {
      this.loading = false;
      this.showMessage('danger', 'Ha ocurrido un error al intentar obtener las novedades.');
    });
  }

  /**
   * Inicializa el formulario de novedades
   */
  initForm(): void {
    const urlRegex = /^(?:http(s)?:\/\/)?[\w.-]+(?:\.[\w\.-]+)+[\w\-\._~:/?#[\]@!\$&'\(\)\*\+,;=.]+$/;

    this.form = new FormGroup({
      title: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.title : '',
        disabled: false
      }, [Validators.maxLength(300)]),
      visibleEttChk: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.visibleEttChk : false,
        disabled: false
      }, [this.validatorVisibleToVn]),
      visibleRpsChk: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.visibleRpsChk : false,
        disabled: false
      }, [this.validatorVisibleToVn]),
      visibleVexChk: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.visibleVexChk : false,
        disabled: false
      }, [this.validatorVisibleToVn]),
      visibleTechChk: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.visibleTechChk : false,
        disabled: false
      }, [this.validatorVisibleToVn]),
      visibleProfChk: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.visibleProfChk : false,
        disabled: false
      }, [this.validatorVisibleToVn]),
      visibleFunChk: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.visibleFunChk : false,
        disabled: false
      }, [this.validatorVisibleToVn]),
      visibleRpoChk: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.visibleRpoChk : false,
        disabled: false
      }, [this.validatorVisibleToVn]),
      file: new FormControl({
        value: '',
        disabled: false
      }, [this.validatorFileVn]),
      url: new FormControl({
        value: this.selectedNovelty ? this.selectedNovelty.url : '',
        disabled: false
      }, [Validators.pattern(urlRegex)]),
      begDate: new FormControl({
        value: this.selectedNovelty ? moment(this.selectedNovelty.begDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
        disabled: false
      }, [this.validatorBegDateVn]),
      endDate: new FormControl({
        value: this.selectedNovelty ? moment(this.selectedNovelty.endDate, 'DD/MM/YYYY').format('YYYY-MM-DD') : '',
        disabled: false
      }, [this.validatorEndDateVn])
    });
  }

  /**
   * Inicializa el formulario de registro
   */
  throwNewNolvety(): void {
    this.dropdown.close();
    this.selectedNovelty = null;
    this.initForm();
    this.fileContent = [];
    this.fileName = '';
    this.titleForm = 'Añadir Novedad';
  }

  /**
   * Inicializa el formulario de edición
   */
  throwEditNovelty(): void {
    this.dropdown.close();
    this.initForm();
    this.fileContent = [];
    this.fileName = '';
    this.titleForm = 'Modificar Novedad';
  }

  /**
   * Muestra la modal para confirmar eliminación de novedad
   */
  throwDeleteNovelty(): void {
    this.dropdown.close();
    this.deleteNoveltyDialog.open();
  }

  /**
   * Almacena en variable el archivo seleccionado
   */
  changeFile(event) {
    const files = (event.target as HTMLInputElement).files;
    if (files.length > 1) {
      this.form.controls['file'].setValue('');
      this.fileContent = [];
      this.fileName = '';
    } else {
      const reader = new FileReader();
      reader.readAsArrayBuffer(files[0]);

      reader.onloadend = (evt) => {
        const target = evt.target as FileReader;
        if (target.readyState === FileReader.DONE) {
          const arrayBuffer = target.result as ArrayBuffer;
          const bytes = new Uint8Array(arrayBuffer);

          this.fileContent = [];
          this.fileName = files[0]['name'];

          for (const i in bytes) {
            this.fileContent.push(bytes[i])
          }
        }
      }
    }
  }

  /**
   * Marca la novedad seleccionada
   * @param novelty Novedad seleccionada
   */
  checkNovelty(novelty: NoveltyModel): void {
    if (this.selectedNovelty && this.selectedNovelty.noveltyId === novelty.noveltyId) {
      this.selectedNovelty = null;
    }
    else {
      this.selectedNovelty = novelty;
    }
  }

  /**
   * Guarda la información de la novedad
   */
  saveNovelty(): void {
    let noveltyModel = new NoveltyModel();
    noveltyModel.title = this.form.get('title').value;
    noveltyModel.visibleEttChk = this.form.get('visibleEttChk').value;
    noveltyModel.visibleRpsChk = this.form.get('visibleRpsChk').value;
    noveltyModel.visibleVexChk = this.form.get('visibleVexChk').value;
    noveltyModel.visibleTechChk = this.form.get('visibleTechChk').value;
    noveltyModel.visibleProfChk = this.form.get('visibleProfChk').value;
    noveltyModel.visibleFunChk = this.form.get('visibleFunChk').value;
    noveltyModel.visibleRpoChk = this.form.get('visibleRpoChk').value;
    noveltyModel.url = this.form.get('url').value;
    noveltyModel.fileContent = this.fileContent;
    noveltyModel.attachedName = this.fileName;
    noveltyModel.begDate = moment(this.form.get('begDate').value, 'YYYY-MM-DD').format('MM-DD-YYYY');
    noveltyModel.endDate = moment(this.form.get('endDate').value, 'YYYY-MM-DD').format('MM-DD-YYYY');

    this.loading = true;
    this.clearMessage();
    if (this.selectedNovelty) {
      this.noveltyService.update(noveltyModel, this.selectedNovelty.noveltyId).subscribe(result => {
        if (result.status) {
          this.form = null;
          this.loading = false;
          this.getInitData();
          this.showMessage('success', 'La novedad ha sido modificada con éxito.');
        }
      }, () => {
        this.loading = false;
        this.showMessage('danger', 'Ha ocurrido un error al intentar modificar la novedad.');
      });
    }
    else {
      this.noveltyService.create(noveltyModel).subscribe(result => {
        if (result.status) {
          this.form = null;
          this.loading = false;
          this.getInitData();
          this.showMessage('success', 'La novedad ha sido registrada con éxito.');
        }
      }, () => {
        this.loading = false;
        this.showMessage('danger', 'Ha ocurrido un error al intentar guardar la novedad.');
      });
    }
  }

  /**
   * Elimina una novedad
   */
  deleteNovelty(): void {
    this.loading = true;
    this.noveltyService.delete(this.selectedNovelty.noveltyId).subscribe(result => {
      if (result.status) {
        this.loading = false;
        this.getInitData();
        this.showMessage('success', 'La novedad ha sido eliminada con éxito.')
      }
    }, () => {
      this.loading = false;
      this.showMessage('danger', 'Ha ocurrido un error al intentar eliminar la novedad.');
    });
  }

  /**
   * Cancela y cierra el formulario
   */
  cancelFrmNovelty(): void {
    this.form = null;
  }

  cancelFile(): void {
    this.fileContent = [];
    this.fileName = '';
    this.form.get('file').setValue('');
  }

  /**
   * Gestiona el componente de paginación
   * @param page Página a mostrar
   */
  pagination(page: number): void {
    if (page !== this.paginationParams.currentPage) {
      this.paginationParams.currentPage = page;
      this.paginationParams.currentIndex = page - 1;
    }
  }

  /**
   * Validación para extensiones de documentos permitidos
   */
  validatorFileVn: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    if (this.form && typeof control.value == 'string' && control.value) {
      const extensionsPermitted = 'ppt,PPT,pptx,PPTX,doc,DOC,docx,DOCX,xls,xlsx,XLS,XLSX,pdf,PDF';
      const extensionSplitted = control.value.split('.');

      if (extensionSplitted && extensionSplitted.length > 0) {
        const extensionFile = extensionSplitted[extensionSplitted.length - 1];
        const existExtension = extensionsPermitted.includes(extensionFile);

        return existExtension ? null : { fileVn: true };
      }
    }

    return null;
  }

  /**
   * Valida que la fecha desde no sea mayor que la fecha hasta
   */
  validatorBegDateVn: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    if (this.form) {
      let date = null;
      if (typeof control.value == 'string') {
        date = control.value != null ? moment(control.value) : null;
      } else {
        date = moment(control.value);
      }

      const endDate = this.form.get('endDate').value ? moment(this.form.get('endDate').value).toDate() : null;
      if (endDate) {
        if (date != null && date.isAfter(endDate, 'day')) {
          return { begDateVn: true };
        } else if (control.errors) {
          control.errors.begDateVn = false;
        }

        if (this.form.get('endDate').errors && this.form.get('endDate').errors.endDateVn) {
          this.form.get('endDate').updateValueAndValidity();
        }
      }
    }

    return null;
  }

  /**
   * Valida que la fecha hasta no sea menor que la fecha desde
   */
  validatorEndDateVn: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    if (this.form) {
      let date = null;
      if (typeof control.value == 'string') {
        date = control.value != null ? moment(control.value) : null;
      } else {
        date = moment(control.value);
      }

      const begDate = this.form.get('begDate').value ? moment(this.form.get('begDate').value).toDate() : null;
      if (begDate) {
        if (date != null && date.isBefore(begDate, 'day')) {
          return { endDateVn: true };
        } else if (control.errors) {
          control.errors.endDateVn = false;
        }

        if (this.form.get('begDate').errors && this.form.get('begDate').errors.begDateVn) {
          this.form.get('begDate').updateValueAndValidity();
        }
      }
    }

    return null;
  }

  /**
   * Valida que se haya seleccionado al menos un check de visible para
   */
  validatorVisibleToVn: ValidatorFn = (control: FormGroup): ValidationErrors | null => {
    if (this.form) {
      const visibleEttChk = this.form.get('visibleEttChk').value;
      const visibleRpsChk = this.form.get('visibleRpsChk').value;
      const visibleVexChk = this.form.get('visibleVexChk').value;
      const visibleTechChk = this.form.get('visibleTechChk').value;
      const visibleProfChk = this.form.get('visibleProfChk').value;
      const visibleFunChk = this.form.get('visibleFunChk').value;
      const visibleRpoChk = this.form.get('visibleRpoChk').value;

      if (!visibleEttChk && !visibleRpsChk && !visibleVexChk && !visibleTechChk
        && !visibleProfChk && !visibleFunChk && !visibleRpoChk) {
        return { visibleTo: true };
      }
      else {
        if (control.errors) {
          control.errors.visibleTo = false;
        }
      }

      if (this.form.get('visibleEttChk').errors && this.form.get('visibleEttChk').errors.visibleTo) {
        this.form.get('visibleEttChk').updateValueAndValidity();
      }
      if (this.form.get('visibleRpsChk').errors && this.form.get('visibleRpsChk').errors.visibleTo) {
        this.form.get('visibleRpsChk').updateValueAndValidity();
      }
      if (this.form.get('visibleVexChk').errors && this.form.get('visibleVexChk').errors.visibleTo) {
        this.form.get('visibleVexChk').updateValueAndValidity();
      }
      if (this.form.get('visibleTechChk').errors && this.form.get('visibleTechChk').errors.visibleTo) {
        this.form.get('visibleTechChk').updateValueAndValidity();
      }
      if (this.form.get('visibleProfChk').errors && this.form.get('visibleProfChk').errors.visibleTo) {
        this.form.get('visibleProfChk').updateValueAndValidity();
      }
      if (this.form.get('visibleFunChk').errors && this.form.get('visibleFunChk').errors.visibleTo) {
        this.form.get('visibleFunChk').updateValueAndValidity();
      }
      if (this.form.get('visibleRpoChk').errors && this.form.get('visibleRpoChk').errors.visibleTo) {
        this.form.get('visibleRpoChk').updateValueAndValidity();
      }
    }
    return null;
  }

  /**
   * Muestra mensaje en pantalla
   * @param type tipo de mensaje (danger, success, info, warning)
   * @param body contenido del mensaje a mostrar
   */
  showMessage(type: string, body: string): void {
    this.message = {
      show: true,
      body: body,
      type: type
    };
    window.scroll(0, 0);

    // Limpia el mensaje después de 5 segundos
    window.clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      this.clearMessage();
    }, 5000);

  }

  /**
   * Limpia los mensajes de pantalla
   */
  clearMessage(): void {
    this.message = {
      show: false,
      body: '',
      type: ''
    };
  }

  /**
   * Permite ordenar la grilla
   * @param column columna a ordenar
   */
  orderBy(column: string) {
    this.pagination(0);

    if (this.currentFilterParams.properties != column) {
      this.currentFilterParams = {
        properties: '',
        typeOrder: ''
      };
    }

    this.currentFilterParams.properties = column;

    if (this.currentFilterParams.typeOrder == '') {
      this.currentFilterParams.typeOrder = 'ASC';
    } else if (this.currentFilterParams.typeOrder == 'ASC') {
      this.currentFilterParams.typeOrder = 'DESC';
    } else {
      this.currentFilterParams.properties = 'iDate';
      this.currentFilterParams.typeOrder = 'DESC';
    }

    this.sortBy();
  }

  /**
   * Ordena de acuerdo a los parémetros seteados
   */
  sortBy() {
    if (['begDate', 'endDate'].includes(this.currentFilterParams.properties)) {
      if (this.currentFilterParams.typeOrder === 'ASC') {
        this.novelties = this.novelties.sort((a, b) => moment(a[this.currentFilterParams.properties], 'DD/MM/YYYY').valueOf() - moment(b[this.currentFilterParams.properties], 'DD/MM/YYYY').valueOf());
      }
      else {
        this.novelties = this.novelties.sort((a, b) => moment(b[this.currentFilterParams.properties], 'DD/MM/YYYY').valueOf() - moment(a[this.currentFilterParams.properties], 'DD/MM/YYYY').valueOf());
      }
    }
    else {
      if (this.currentFilterParams.typeOrder === 'ASC') {
        this.novelties = this.novelties.sort((a, b) => a[this.currentFilterParams.properties].localeCompare(b[this.currentFilterParams.properties]));
      }
      else {
        this.novelties = this.novelties.sort((a, b) => b[this.currentFilterParams.properties].localeCompare(a[this.currentFilterParams.properties]));
      }
    }

    let count = 0;
    for (let i = 0; i < this.novelties.length; i += this.paginationParams.pageMaxSize) {
      let nextIndex = Math.min(this.paginationParams.pageMaxSize * (count + 1), this.novelties.length);
      let currentList = this.novelties.slice(i, nextIndex);
      this.noveltiesPagination[count] = currentList;
      count++;
    }
  }

  /**
   * Acción para volver a la ventana principal
   */
  backToHome() {
    this.router.navigate(["/dashboard"]);
  }
}
